import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { setLanguage } from '../utils/helpers';
import { SUPPORTED_LANGUAGES } from '../i18n/languages';

// #95: the registry of i18n/languages
const languages = SUPPORTED_LANGUAGES;

interface Props {
  // #69: on the status page only the language changes; the page reloads the
  // labels of its verified shop itself (no remembered-shop request)
  staticOnly?: boolean;
  // #493 (S3): the returns flow also loads the start-page texts of the URL
  // shop for the new language (awaited, bounded) before applying it
  startTexts?: boolean;
}

// #105 (S1, plan D6): the language pill of the portal header (mockup
// .p-lang): the same native select, a visually hidden label, a globe and a
// caret icon. The select is transparent over the whole pill (review r1).
const LanguageSwitcher: React.FC<Props> = ({
  staticOnly = false,
  startTexts = false,
}) => {
  const { t, i18n } = useTranslation(['translation', 'dynamic']);
  const id = `xc-lang-${useId().replace(/:/g, '')}`;
  const current =
    languages.find(l => l.code === i18n.language)?.name ??
    (i18n.language ?? '').toUpperCase();

  return (
    <div className="xc-lang">
      <label htmlFor={id} className="xc-visually-hidden">
        {t('header.language')}
      </label>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c3 3.5 3 14.5 0 18M12 3c-3 3.5-3 14.5 0 18" />
      </svg>
      <span className="xc-lang__value" aria-hidden="true">
        {current}
      </span>
      {/* transparent, covers the whole pill: any click opens it */}
      <select
        id={id}
        className="xc-lang__select"
        value={i18n.language}
        onChange={e =>
          setLanguage(e.target.value, {
            withShopLabels: !staticOnly,
            withStartTexts: startTexts && !staticOnly,
          })
        }
      >
        {languages.map(lang => (
          <option key={lang.code} value={lang.code}>
            {lang.name}
          </option>
        ))}
      </select>
      <svg
        className="xc-lang__caret"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
  );
};

export default LanguageSwitcher;
